import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Kbd } from '@/components/ui/Kbd';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { TenantSwitcher } from '@/components/layout/TenantSwitcher';
import { cn } from '@/lib/cn';
import { useRequirentePortal } from '@/hooks/useRequirentePortal';
import { useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';

export function Topbar() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { isRequirenteOnly } = useRequirentePortal();

  const openPalette = () => dispatch(uiActions.setCommandPaletteOpen(true));
  const openMobileNav = () => dispatch(uiActions.setMobileSidebarOpen(true));

  const submitSearch = () => {
    const q = query.trim().slice(0, 128);
    if (q) {
      navigate(`/search?q=${encodeURIComponent(q)}`);
      return;
    }
    openPalette();
  };

  return (
    <header className="safe-topbar flex shrink-0 items-center gap-2 border-b px-3 md:gap-4 md:px-6">
      <Button
        variant="ghost"
        size="sm"
        className="shrink-0 md:hidden"
        onClick={openMobileNav}
        aria-label="Abrir menú de navegación"
      >
        <Menu className="h-5 w-5" />
      </Button>

      <form
        className={isRequirenteOnly ? 'hidden' : 'flex min-w-0 flex-1 items-center gap-2'}
        onSubmit={(event) => {
          event.preventDefault();
          submitSearch();
        }}
      >
        {!isRequirenteOnly && (
          <>
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value.slice(0, 128))}
                placeholder="Buscar..."
                className="pl-9 pr-2 sm:pr-20"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 hidden -translate-y-1/2 items-center gap-1 sm:inline-flex"
                onClick={openPalette}
                aria-label="Abrir paleta de comandos (Ctrl+K o ⌘K)"
              >
                <Kbd>⌘</Kbd>
                <Kbd>K</Kbd>
              </button>
            </div>
            <Button type="submit" variant="outline" size="sm" className="hidden shrink-0 sm:inline-flex">
              Buscar
            </Button>
          </>
        )}
      </form>

      <div className={cn('flex shrink-0 items-center gap-1 sm:gap-3', isRequirenteOnly && 'ml-auto')}>
        <TenantSwitcher />
        <NotificationBell />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}
